import clsx from "clsx";
import Autocomplete from "ui/common/components/input/Autocomplete/Autocomplete";
import { useDebounce } from "@commonlibs";
import { useState, useEffect } from "react";
import useFindApprovers from "features/release-strategy/hooks/useFindApprovers";
import { ApproversAutocompleteProps } from "./ApproversAutocomplete.types";

export default function ApproversAutocomplete({
    className,
    filter,
    onChange,
    onSearchChange,
    defaultSearch,
    ...rest
}: ApproversAutocompleteProps): JSX.Element {
    const [search, setSearch] = useState(defaultSearch || "");

    const { value, handleChange, debouncing } = useDebounce<string>(defaultSearch || "", { callback: setSearch });
    const [selectedValue, setSelectedValue] = useState<number | undefined>(undefined); // skipcq: JS-W1042

    useEffect(() => {
        if (defaultSearch !== undefined && defaultSearch !== search) {
            setSearch(defaultSearch);
            handleChange(defaultSearch);
        }
    }, [defaultSearch]);

    useEffect(() => {
        if (onSearchChange && search !== defaultSearch) {
            onSearchChange(search);
        }
    }, [search]);

    const { data: { items = [] } = {}, isFetching } = useFindApprovers({ query: search });

    const filteredItems = filter ? items.filter(filter) : items;

    function handleSelect(value: number | null) {
        if (value == null) {
            onChange?.(null);
            return;
        }
        setSelectedValue(value);

        const selectedApprover = filteredItems.find((item) => item.id === value);

        onChange?.(selectedApprover ?? null);
    }

    return (
        <Autocomplete
            className={clsx("approvers-select", className)}
            value={selectedValue}
            expandable
            isLoading={isFetching || debouncing}
            onSearchChange={handleChange}
            search={value}
            options={filteredItems.map((item) => ({
                value: item.id,
                label: item.email ?? "",
            }))}
            onChange={handleSelect}
            {...rest}
        />
    );
}
